<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import type { IMessage } from '@/pages/ai-talk/types'

import AiOnboardingAssistant from '@/components/AiOnboardingAssistant.vue'
import { Icon } from '@/components/icon'
import OnboardingHelpRow from '@/components/onboarding/help-row.vue'
import OnboardingStepSidebarGoalFirst from '@/components/OnboardingStepSidebarGoalFirst.vue'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAmplitude } from '@/composables/use-amplitude'
import { useLocalizedOnboardingPic } from '@/composables/use-localized-onboarding-pic'
import { exampleTalks } from '@/pages/ai-talk/data/talks'
import { useOnboardingAnswerSave, useV7OnboardingTaskInit } from '@/services/api/platform.api'
import { useLanguageStore } from '@/stores/language'
import { useOnboardingSetupStore } from '@/stores/onboarding-setup'

definePage({
  path: '/onboarding',
  meta: {
    layout: false,
  },
})
const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n({ useScope: 'global' })
const languageStore = useLanguageStore()
const { getOnboardingPic } = useLocalizedOnboardingPic()
const isZhLocale = computed(() => locale.value === 'zh')
function toggleOnboardingLocale() {
  const nextLocale = locale.value === 'zh' ? 'en' : 'zh'
  locale.value = nextLocale
  languageStore.currentLocale = nextLocale
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('locale', nextLocale)
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = nextLocale
  }
}
const showPhase1 = ref(true)
const industryOptions = [
  { value: 'Home & living', labelKey: 'onboarding.questionnaire.industry.homeLiving' },
  { value: 'Fashion & apparel', labelKey: 'onboarding.questionnaire.industry.fashion' },
  { value: 'Beauty & personal care', labelKey: 'onboarding.questionnaire.industry.beauty' },
  { value: 'Baby & kids', labelKey: 'onboarding.questionnaire.industry.baby' },
  { value: 'Food & beverage', labelKey: 'onboarding.questionnaire.industry.food' },
  { value: 'Electronics', labelKey: 'onboarding.questionnaire.industry.electronics' },
  { value: 'Health', labelKey: 'onboarding.questionnaire.industry.health' },
  { value: 'Sports & outdoor', labelKey: 'onboarding.questionnaire.industry.sports' },
  { value: 'Pet', labelKey: 'onboarding.questionnaire.industry.pet' },
  { value: 'Other', labelKey: 'onboarding.questionnaire.industry.other' },
]
const selectedIndustry = ref<string | null>(null)
const industryOther = ref('')
function selectIndustry(opt: string) { selectedIndustry.value = opt }
const sourceOptions = [
  { value: 'Shopify App Store', labelKey: 'onboarding.questionnaire.source.shopifyAppStore' },
  { value: 'RedNote', labelKey: 'onboarding.questionnaire.source.redNote' },
  { value: 'Social Media (LinkedIn, Twitter, Xiaohongshu)', labelKey: 'onboarding.questionnaire.source.socialMedia' },
  { value: 'Search Engine (Google, Bing)', labelKey: 'onboarding.questionnaire.source.searchEngine' },
  { value: 'Referral Site: G2, etc.', labelKey: 'onboarding.questionnaire.source.referral' },
  { value: 'Friends', labelKey: 'onboarding.questionnaire.source.friends' },
  { value: 'Other', labelKey: 'onboarding.questionnaire.source.other' },
]
const selectedSource = ref<string | null>(null)
const sourceOther = ref('')
function selectSource(opt: string) { selectedSource.value = opt }
const sourcePlaceholder = computed(() => {
  switch (selectedSource.value) {
    case 'Shopify App Store':
      return t('onboarding.questionnaire.source.placeholders.shopifyAppStore')
    case 'RedNote':
      return t('onboarding.questionnaire.source.placeholders.redNote')
    case 'Social Media (LinkedIn, Twitter, Xiaohongshu)':
      return t('onboarding.questionnaire.source.placeholders.socialMedia')
    case 'Search Engine (Google, Bing)':
      return t('onboarding.questionnaire.source.placeholders.searchEngine')
    case 'Referral Site: G2, etc.':
      return t('onboarding.questionnaire.source.placeholders.referral')
    case 'Friends':
      return t('onboarding.questionnaire.source.placeholders.friends')
    case 'Other':
      return t('onboarding.questionnaire.source.placeholders.other')
    default:
      return t('onboarding.questionnaire.source.placeholders.default')
  }
})
const painPointOptions = [
  { value: 'Identify more abandoners and recover more email revenue', labelKey: 'onboarding.questionnaire.goals.abandoners' },
  { value: 'Retarget more ad audiences with server-side tracking', labelKey: 'onboarding.questionnaire.goals.retarget' },
  { value: 'Measure true ROAS with AI attribution for both view + clicks', labelKey: 'onboarding.questionnaire.goals.roas' },
  { value: 'De-anonymize visitors and grow email list', labelKey: 'onboarding.questionnaire.goals.deanon' },
  { value: 'Track and attribute influencer performance', labelKey: 'onboarding.questionnaire.goals.influencer' },
  { value: 'I have other goals', labelKey: 'onboarding.questionnaire.goals.other' },
]
const selectedPainPoints = ref<string[]>([])
const painOther = ref('')
function togglePainPoint(pt: string) {
  if (selectedPainPoints.value[0] === pt) {
    selectedPainPoints.value = []
    return
  }
  selectedPainPoints.value = [pt]
}
const painPointMessageMap: Record<string, string> = {
  'Measure true ROAS with AI attribution for both view + clicks':
    'onboarding.questionnaire.goalMessages.roas',
  'Retarget more ad audiences with server-side tracking':
    'onboarding.questionnaire.goalMessages.retarget',
  'Identify more abandoners and recover more email revenue':
    'onboarding.questionnaire.goalMessages.abandoners',
  'De-anonymize visitors and grow email list':
    'onboarding.questionnaire.goalMessages.deanon',
  'Track and attribute influencer performance':
    'onboarding.questionnaire.goalMessages.influencer',
}
const selectedPainMessages = computed(() =>
  selectedPainPoints.value
    .map((pt: string) => painPointMessageMap[pt])
    .filter((m: string | undefined): m is string => Boolean(m))
    .map(key => t(key)),
)
const painImageMap: Record<string, string> = {
  'Identify more abandoners and recover more email revenue': 'onboarding-abandoner-1.png',
  'Retarget more ad audiences with server-side tracking': 'onboarding-retarget-1.png',
  'Measure true ROAS with AI attribution for both view + clicks': 'onboarding-attribution-1.png',
  'Track and attribute influencer performance': 'onboarding-influencer-1.png',
  'De-anonymize visitors and grow email list': 'onboarding-de-anonymize-1.png',
  'I have other goals': 'onboarding-other-1.png',
}
const painContinueEventMap: Record<string, string> = {
  'Identify more abandoners and recover more email revenue': 'onboarding_goal_abandoner_continue_click',
  'Retarget more ad audiences with server-side tracking': 'onboarding_goal_retarget_continue_click',
  'Measure true ROAS with AI attribution for both view + clicks': 'onboarding_goal_roas_continue_click',
  'De-anonymize visitors and grow email list': 'onboarding_goal_de-anonymize_continue_click',
  'Track and attribute influencer performance': 'onboarding_goal_influencer_continue_click',
  'I have other goals': 'onboarding_goal_other_continue_click',
}
const painImageSrc = computed(() => getOnboardingPic(painImageMap[selectedPainPoints.value[0]] || 'onboarding-other-1.png'))
const toolsImageSrc = computed(() =>
  selectedPainPoints.value.includes('Identify more abandoners and recover more email revenue')
    ? getOnboardingPic('onboarding-abandoner-2.png')
    : getOnboardingPic('onboarding-tool.png'),
)
interface ToolItem { id: string, labelKey: string, icon: string, descKey: string }
const painPointToolMap: Record<string, ToolItem[]> = {
  'Measure true ROAS with AI attribution for both view + clicks': [
    { id: 'meta-ads', labelKey: 'onboarding.questionnaire.tools.meta', icon: 'integrations/facebook_filling', descKey: 'onboarding.questionnaire.toolDescriptions.meta' },
    { id: 'google-ads', labelKey: 'onboarding.questionnaire.tools.google', icon: 'integrations/google_linear', descKey: 'onboarding.questionnaire.toolDescriptions.google' },
    { id: 'klaviyo', labelKey: 'onboarding.questionnaire.tools.klaviyo', icon: 'integrations/klaviyo_filling', descKey: 'onboarding.questionnaire.toolDescriptions.klaviyo' },
    { id: 'tiktok-ads', labelKey: 'onboarding.questionnaire.tools.tiktok', icon: 'integrations/tiktok_filling', descKey: 'onboarding.questionnaire.toolDescriptions.tiktok' },
    { id: 'bing-ads', labelKey: 'onboarding.questionnaire.tools.bing', icon: 'integrations/bing_linear', descKey: 'onboarding.questionnaire.toolDescriptions.bing' },
    { id: 'linkedin', labelKey: 'onboarding.questionnaire.tools.linkedin', icon: 'integrations/linkedin_filling', descKey: 'onboarding.questionnaire.toolDescriptions.linkedin' },
    { id: 'pinterest-ads', labelKey: 'onboarding.questionnaire.tools.pinterest', icon: 'integrations/pinterest_filling', descKey: 'onboarding.questionnaire.toolDescriptions.pinterest' },
    { id: 'snapchat-ads', labelKey: 'onboarding.questionnaire.tools.snapchat', icon: 'integrations/snapchat_filling', descKey: 'onboarding.questionnaire.toolDescriptions.snapchat' },
    // 暂时屏蔽
    // { id: 'criteo', name: 'Criteo', icon: 'integrations/criteo_filling', desc: 'Commerce media retargeting' },
  ],
  'Retarget more ad audiences with server-side tracking': [
    { id: 'meta-ads', labelKey: 'onboarding.questionnaire.tools.meta', icon: 'integrations/facebook_filling', descKey: 'onboarding.questionnaire.toolDescriptions.meta' },
    { id: 'google-ads', labelKey: 'onboarding.questionnaire.tools.google', icon: 'integrations/google_linear', descKey: 'onboarding.questionnaire.toolDescriptions.google' },
    { id: 'klaviyo', labelKey: 'onboarding.questionnaire.tools.klaviyo', icon: 'integrations/klaviyo_filling', descKey: 'onboarding.questionnaire.toolDescriptions.klaviyo' },
    { id: 'tiktok-ads', labelKey: 'onboarding.questionnaire.tools.tiktok', icon: 'integrations/tiktok_filling', descKey: 'onboarding.questionnaire.toolDescriptions.tiktok' },
    { id: 'bing-ads', labelKey: 'onboarding.questionnaire.tools.bing', icon: 'integrations/bing_linear', descKey: 'onboarding.questionnaire.toolDescriptions.bing' },
    { id: 'pinterest-ads', labelKey: 'onboarding.questionnaire.tools.pinterest', icon: 'integrations/pinterest_filling', descKey: 'onboarding.questionnaire.toolDescriptions.pinterest' },
    { id: 'snapchat-ads', labelKey: 'onboarding.questionnaire.tools.snapchat', icon: 'integrations/snapchat_filling', descKey: 'onboarding.questionnaire.toolDescriptions.snapchat' },
    { id: 'omnisend', labelKey: 'onboarding.questionnaire.tools.omnisend', icon: 'integrations/omnisend_filling', descKey: 'onboarding.questionnaire.toolDescriptions.omnisend' },
    // 暂时屏蔽
    // { id: 'mailchimp', name: 'Mailchimp', icon: 'integrations/mailchimp_linear', desc: 'Email marketing' },
  ],
  'Track and attribute influencer performance': [
  ],
  'De-anonymize visitors and grow email list': [
    { id: 'meta-ads', labelKey: 'onboarding.questionnaire.tools.meta', icon: 'integrations/facebook_filling', descKey: 'onboarding.questionnaire.toolDescriptions.meta' },
    { id: 'google-ads', labelKey: 'onboarding.questionnaire.tools.google', icon: 'integrations/google_linear', descKey: 'onboarding.questionnaire.toolDescriptions.google' },
    { id: 'klaviyo', labelKey: 'onboarding.questionnaire.tools.klaviyo', icon: 'integrations/klaviyo_filling', descKey: 'onboarding.questionnaire.toolDescriptions.klaviyo' },
    { id: 'tiktok-ads', labelKey: 'onboarding.questionnaire.tools.tiktok', icon: 'integrations/tiktok_filling', descKey: 'onboarding.questionnaire.toolDescriptions.tiktok' },
    { id: 'pinterest-ads', labelKey: 'onboarding.questionnaire.tools.pinterest', icon: 'integrations/pinterest_filling', descKey: 'onboarding.questionnaire.toolDescriptions.pinterest' },
    { id: 'ga4', labelKey: 'onboarding.questionnaire.tools.ga4', icon: 'integrations/ga4', descKey: 'onboarding.questionnaire.toolDescriptions.ga4' },
  ],
  'Identify more abandoners and recover more email revenue': [
    { id: 'klaviyo', labelKey: 'onboarding.questionnaire.tools.klaviyo', icon: 'integrations/klaviyo_filling', descKey: 'onboarding.questionnaire.toolDescriptions.klaviyo' },
    { id: 'omnisend', labelKey: 'onboarding.questionnaire.tools.omnisend', icon: 'integrations/omnisend_filling', descKey: 'onboarding.questionnaire.toolDescriptions.omnisend' },
    // 暂时屏蔽
    // { id: 'mailchimp', name: 'Mailchimp', icon: 'integrations/mailchimp_linear', desc: 'Email marketing' },
  ],
  // Removed: 'Reduce unknown traffic and get richer analytics'
}
const recommendedTools = computed<ToolItem[]>(() => {
  const map = new Map<string, ToolItem>(); for (const pt of selectedPainPoints.value) {
    const tools = painPointToolMap[pt] || []; for (const t of tools) {
      if (!map.has(t.id))
        map.set(t.id, t)
    }
  } return Array.from(map.values())
})
// 追加 UI 列表：仅追加一个全局 Other 选项
const recommendedToolsUI = computed<ToolItem[]>(() => {
  const out: ToolItem[] = []
  if (
    selectedPainPoints.value.includes('De-anonymize visitors and grow email list')
    || selectedPainPoints.value.includes('I have other goals')
  ) {
    // Show all tools when specific pain point or 'Other' is selected
    const map = new Map<string, ToolItem>()
    Object.values(painPointToolMap).forEach((list) => {
      list.forEach((t) => {
        if (!map.has(t.id))
          map.set(t.id, t)
      })
    })
    out.push(...Array.from(map.values()))
  }
  else {
    out.push(...recommendedTools.value)
  }
  return out
})

const selectedTools = ref<string[]>([])
const showCustomToolFor = ref<string | null>(null)
const customToolName = ref('')
const otherSelected = ref(false)
const singleToolOnly = computed(() => true)
function toggleTool(id: string) {
  if (singleToolOnly.value) {
    selectedTools.value = [id]
  }
  else {
    const i = selectedTools.value.indexOf(id)
    if (i >= 0)
      selectedTools.value.splice(i, 1)
    else selectedTools.value.push(id)
  }
}
function addCustomTool() {
  const name = customToolName.value.trim(); if (!name)
    return
  const customId = `custom:${name}`
  if (!selectedTools.value.includes(customId))
    selectedTools.value.push(customId)
  customToolName.value = ''
  showCustomToolFor.value = null
  otherSelected.value = false
  updateTalkSelectedValues('tools')
}
function cancelCustomTool() {
  customToolName.value = ''
  showCustomToolFor.value = null
  otherSelected.value = false
}
const canContinue = computed(() => {
  const painOk = selectedPainPoints.value.length > 0 && (
    !selectedPainPoints.value.includes('I have other goals') || painOther.value.trim().length > 0
  )
  const toolsOk = selectedTools.value.length > 0
  return painOk && toolsOk
})

// Amplitude tracking
const { track } = useAmplitude()
const continueBtnRef = ref<HTMLButtonElement | null>(null)
let continueObserver: IntersectionObserver | null = null
let continueViewTracked = false

onMounted(() => {
  const q = route.query?.q as string | undefined
  if (q) {
    const idx = questionOrder.indexOf(q as any)
    if (idx >= 0)
      currentQuestionIndex.value = idx
  }

  // 首先从 store 恢复问卷状态（优先于 localStorage）
  if (setupStore.questionnaireState) {
    const qs = setupStore.questionnaireState
    // 恢复当前问题索引 (如果 URL 没有指定 q 参数)
    if (!q && qs.currentQuestion) {
      // 'pain-complete' 表示用户在 pain 步骤完成后跳过了 tools（De-anonymize/Influencer/Other 流程）
      // 恢复到 pain 步骤让用户重新点击 Continue
      const restoreQuestion = qs.currentQuestion === 'pain-complete' ? 'pain' : qs.currentQuestion
      const idx = questionOrder.indexOf(restoreQuestion as any)
      if (idx >= 0)
        currentQuestionIndex.value = idx
    }
    // 恢复问卷字段
    if (setupStore.source) {
      selectedSource.value = setupStore.source
      sourceOther.value = qs.sourceDetail || ''
    }
    if (setupStore.painPoints && setupStore.painPoints.length > 0) {
      selectedPainPoints.value = [...setupStore.painPoints]
      painOther.value = qs.painOther || ''
    }
    if (setupStore.toolsSelected && setupStore.toolsSelected.length > 0) {
      selectedTools.value = [...setupStore.toolsSelected]
    }
    if (setupStore.industry) {
      selectedIndustry.value = setupStore.industry
    }
    updateTalkSelectedValues('industry')
    updateTalkSelectedValues('source')
    updateTalkSelectedValues('painPoints')
    updateTalkSelectedValues('tools')
  }
  else {
    // 如果 store 没有数据，尝试从 localStorage 恢复
    try {
      const raw = localStorage.getItem('onboarding-answer')
      if (raw) {
        const ans = JSON.parse(raw || '{}')
        selectedIndustry.value = ans.industry || null
        selectedSource.value = ans.source || null
        sourceOther.value = ans.sourceOther || ''
        selectedPainPoints.value = Array.isArray(ans.painPoints) ? ans.painPoints : []
        selectedTools.value = Array.isArray(ans.tools) ? ans.tools : []
        updateTalkSelectedValues('industry')
        updateTalkSelectedValues('source')
        updateTalkSelectedValues('painPoints')
        updateTalkSelectedValues('tools')
      }
    }
    catch (e) {}
  }

  // Pageview for onboarding questionnaire
  track('onboarding_pageview', {
    section: 'Onboarding',
    page: 'questionnaire',
  })

  // Observe Continue button exposure - use nextTick to ensure DOM is ready
  nextTick(() => {
    const el = continueBtnRef.value
    if (el && el instanceof Element && !continueObserver) {
      continueObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && !continueViewTracked) {
          track('onboarding_continue_view', {
            section: 'Onboarding',
            page: 'questionnaire',
            button_text: 'Continue',
            can_continue: canContinue.value,
          })
          continueViewTracked = true
        }
      }, { threshold: 0.6 })
      continueObserver.observe(el)
    }
  })
})

onBeforeUnmount(() => {
  if (continueObserver && continueBtnRef.value) {
    continueObserver.unobserve(continueBtnRef.value)
  }
  continueObserver = null
})
const setupStore = useOnboardingSetupStore()

// API hooks for saving answers and initializing tasks
const { mutateAsync: saveAnswer } = useOnboardingAnswerSave()
const { mutateAsync: initTasks } = useV7OnboardingTaskInit()

// 清除 benchmark onboarding 全套标记
// 用户从 benchmark 来源进入 Goal-First onboarding 后，只要开始进入下一步（或跳过），
// 就清除所有 benchmark 标记，避免后续会话/其他店铺被误判为 benchmark 来源
function clearBenchmarkOnboardingFlag() {
  try {
    localStorage.removeItem('benchmark_onboarding_flag')
    localStorage.removeItem('benchmark_onboarding_pending')
  } catch (_e) {}
}

const showAiAssistant = ref(false)

function handleOpenAiAssistant() {
  // Sync current selections to store so AI has context (don't save to server)
  setupStore.seedFromOnboarding({
    industry: selectedIndustry.value,
    source: selectedSource.value,
    painPoints: [...selectedPainPoints.value],
    toolsSelected: [...selectedTools.value],
  }, false)
  showAiAssistant.value = true
}

async function handlePhase1Continue() {
  // 进入 Goal-First onboarding 正式流程：清除 benchmark 标记，后续会话不再特殊处理
  clearBenchmarkOnboardingFlag()
  // Track click regardless of validation success; include current selections
  track('onboarding_continue_click', {
    section: 'Onboarding',
    page: 'questionnaire',
    button_text: 'Continue',
    can_continue: canContinue.value,
    selected_industry: selectedIndustry.value,
    selected_source: selectedSource.value,
    selected_pain_points: [...selectedPainPoints.value],
    selected_tools: [...selectedTools.value],
    other_tool_input: otherSelected.value ? customToolName.value.trim() : undefined,
  })
  if (currentQuestion.value === 'tools') {
    track('onboarding_tool_continue_click', {
      section: 'Onboarding',
      page: 'questionnaire',
      selected_tool: selectedTools.value[0],
    })
  }
  const painOk = selectedPainPoints.value.length > 0
  const toolsOk = selectedTools.value.length > 0 || (otherSelected.value && customToolName.value.trim().length > 0)
  const sourceOk = !!selectedSource.value && sourceOther.value.trim().length > 0

  if (!painOk || !toolsOk) {
    if (!painOk) {
      toast.info(t('onboarding.toasts.selectGoals'))
    }
    if (!toolsOk) {
      toast.info(t('onboarding.toasts.selectTools'))
    }
    return
  }

  try {
    // 1. 准备答案数据
    // Source details are optional at this stage; will be collected later if missing

    const answerData = {
      industry: selectedIndustry.value,
      industryOther: selectedIndustry.value === 'Other' ? industryOther.value : null,
      source: selectedSource.value,
      sourceOther: selectedSource.value && sourceOther.value ? sourceOther.value : null,
      painPoints: [...selectedPainPoints.value],
      tools: [...selectedTools.value],
    }

    // 2. 将 Phase 1 的选择数据写入引导 store（包含保存到后端）
    await setupStore.seedFromOnboarding({
      industry: selectedIndustry.value,
      source: selectedSource.value,
      painPoints: [...selectedPainPoints.value],
      toolsSelected: [...selectedTools.value],
    })

    // 3. 获取生成的任务 IDs
    const taskIds = setupStore.getAllTaskIds()

    // 4. 暂存答案到本地，后续在 step3 完成时再保存
    try { localStorage.setItem('onboarding-answer', JSON.stringify(answerData)) }
    catch (e) { /* ignore */ }

    // 5. 跳转流程页面（seedFromOnboarding 已经保存到后端，无需再次调用）
    setupStore.openModal()
    if (selectedPainPoints.value.includes('De-anonymize visitors and grow email list')) {
      router.push({ path: '/onboarding/de-anonymize-steps/step1', query: { prev: route.fullPath } })
      return
    }
    const primaryTool = selectedTools.value[0]
    const isInfluencerGoal = selectedPainPoints.value.includes('Track and attribute influencer performance')
    const isDeAnonGoal = selectedPainPoints.value.includes('De-anonymize visitors and grow email list')
    const isOtherGoals = selectedPainPoints.value.includes('I have other goals')
    if (isOtherGoals) {
      router.push({ path: '/onboarding/google-steps/step1', query: { skipOff: '1', prev: route.fullPath } })
      return
    }
    if (isDeAnonGoal) {
      router.push({ path: '/onboarding/de-anonymize-steps/step1', query: { prev: route.fullPath } })
      return
    }
    if (isInfluencerGoal) {
      router.push({ path: '/onboarding/influencer-steps/step1', query: { prev: route.fullPath } })
      return
    }
    if (!isInfluencerGoal && !isDeAnonGoal && !isOtherGoals && (primaryTool === 'other' || otherSelected.value)) {
      router.push({ path: '/onboarding/goal-steps/step3', query: { prev: route.fullPath } })
      return
    }
    if (primaryTool && primaryTool !== 'other') {
      const toolSlugMap: Record<string, string> = {
        'klaviyo': 'klaviyo',
        'omnisend': 'omnisend',
        'meta-ads': 'meta',
        'facebook': 'meta',
        'meta': 'meta',
        'google-ads': 'google',
        'google': 'google',
        'tiktok-ads': 'tiktok',
        'tiktok': 'tiktok',
        'pinterest-ads': 'pinterest',
        'pinterest': 'pinterest',
        'snapchat-ads': 'snapchat',
        'snapchat': 'snapchat',
        'bing-ads': 'bing',
        'bing': 'bing',
        'impact': 'impact',
        'shareasale': 'shareasale',
        'applovin': 'applovin',
        'calltrackingmetrics': 'calltrackingmetrics',
        'linkedin': 'linkedin',
        'ga4': 'ga4',
      }
      const slug = toolSlugMap[primaryTool]
      if (slug === 'klaviyo') {
        router.push({ path: '/onboarding/klaviyo-steps/step1', query: { prev: route.fullPath } })
      }
      else if (slug === 'omnisend') {
        router.push({ path: '/onboarding/omnisend-steps/step1', query: { prev: route.fullPath } })
      }
      else if (slug) {
        if (slug === 'google') {
          const needSkipOff = selectedPainPoints.value.includes('Retarget more ad audiences with server-side tracking')
            || selectedPainPoints.value.includes('Measure true ROAS with AI attribution for both view + clicks')
          if (needSkipOff) {
            router.push({ path: `/onboarding/${slug}-steps/step1`, query: { skipOff: '1', prev: route.fullPath } })
          }
          else {
            router.push({ path: `/onboarding/${slug}-steps/step1`, query: { prev: route.fullPath } })
          }
        }
        else {
          router.push({ path: `/onboarding/${slug}-steps/step1`, query: { prev: route.fullPath } })
        }
      }
      else {
        router.push({ path: '/onboarding/goal-steps/step1', query: { prev: route.fullPath } })
      }
      return
    }
    router.push('/home')
  }
  catch (error) {
    console.error('Failed to save onboarding data:', error)
    toast.error(t('onboarding.toasts.saveFailed'))
  }
}
type QuestionKey = 'source' | 'pain' | 'tools'
const questionOrder: QuestionKey[] = ['pain', 'tools']
const currentQuestionIndex = ref(0)
const flowTotal = computed(() => questionOrder.length + 3)
const currentQuestion = computed<QuestionKey>(() => questionOrder[currentQuestionIndex.value] || 'pain')
const isFirstQuestion = computed(() => currentQuestionIndex.value === 0)
const isLastQuestion = computed(() => currentQuestionIndex.value === questionOrder.length - 1)
const canNext = computed(() => {
  if (currentQuestion.value === 'pain') {
    if (selectedPainPoints.value.includes('I have other goals'))
      return painOther.value.trim().length > 0
    return selectedPainPoints.value.length > 0
  }
  if (currentQuestion.value === 'tools') {
    return selectedTools.value.length > 0
  }
  return false
})
async function nextQuestion() {
  if (!canNext.value)
    return
  if (currentQuestion.value === 'source') {
    track('onboarding_heard_continue_click', {
      section: 'Onboarding',
      page: 'questionnaire',
      selected_source: selectedSource.value,
      source_detail: sourceOther.value.trim(),
    })
    // 保存 source 问题的进度到后端
    await setupStore.saveQuestionnaireProgress({
      source: selectedSource.value,
      sourceDetail: sourceOther.value.trim(),
      currentQuestion: 'pain',
    })
  }
  if (currentQuestion.value === 'pain') {
    const goal = selectedPainPoints.value[0]
    const ev = painContinueEventMap[goal] || 'onboarding_goal_other_continue_click'
    track(ev, {
      section: 'Onboarding',
      page: 'questionnaire',
      selected_goal: goal,
    })

    // 判断是否需要跳过 tools 步骤（De-anonymize、Influencer、Other goals 直接进入各自流程）
    const isDeAnon = selectedPainPoints.value.includes('De-anonymize visitors and grow email list')
    const isInfluencer = selectedPainPoints.value.includes('Track and attribute influencer performance')
    const isOther = selectedPainPoints.value.includes('I have other goals')
    const skipTools = isDeAnon || isInfluencer || isOther

    // 对于跳过 tools 的特殊流程，预设工具为 google-ads 并生成步骤队列
    // 因为这些流程最终都会进入 google-steps，需要提前生成步骤以便后端保存
    if (skipTools) {
      setupStore.toolsSelected = ['google-ads']
      setupStore.useNewOnboarding = true
      setupStore.generateQueue()
    }

    // 保存 pain 问题的进度到后端（包含生成的 steps）
    // 如果跳过 tools 步骤，currentQuestion 标记为 'pain-complete' 而不是 'tools'
    await setupStore.saveQuestionnaireProgress({
      source: selectedSource.value,
      sourceDetail: sourceOther.value.trim(),
      painPoints: [...selectedPainPoints.value],
      painOther: selectedPainPoints.value.includes('I have other goals') ? painOther.value.trim() : null,
      toolsSelected: skipTools ? ['google-ads'] : undefined,
      currentQuestion: skipTools ? 'pain-complete' : 'tools',
    })

    // Skip tools question：直接导航到对应的步骤页面，不调用 handlePhase1Continue
    // （handlePhase1Continue 会调用 seedFromOnboarding 生成工具步骤队列，对于 skip-tools 流程是不需要的）
    if (isDeAnon || isInfluencer || isOther) {
      clearBenchmarkOnboardingFlag() // 进入正式 step 前清除 benchmark 标记
      track('onboarding_continue_click', {
        section: 'Onboarding',
        page: 'questionnaire',
        button_text: 'Continue',
        can_continue: true,
        selected_pain_points: [...selectedPainPoints.value],
      })
      if (isDeAnon) {
        router.push({ path: '/onboarding/de-anonymize-steps/step1', query: { prev: route.fullPath } })
        return
      }
      if (isInfluencer) {
        router.push({ path: '/onboarding/influencer-steps/step1', query: { prev: route.fullPath } })
        return
      }
      router.push({ path: '/onboarding/google-steps/step1', query: { skipOff: '1', prev: route.fullPath } })
      return
    }
  }
  if (currentQuestionIndex.value < questionOrder.length - 1) {
    currentQuestionIndex.value += 1
  }
}

// Default selections when entering each question
watch(currentQuestion, async (q) => {
  await nextTick()
  // Update URL query
  router.replace({ query: { ...route.query, q } })

  if (q === 'source') {
    track('onboarding_heard_view', {
      section: 'Onboarding',
      page: 'questionnaire',
    })
  }
  if (q === 'pain') {
    track('onboarding_goal_view', {
      section: 'Onboarding',
      page: 'questionnaire',
    })
  }
  if (q === 'tools') {
    track('onboarding_tool_view', {
      section: 'Onboarding',
      page: 'questionnaire',
    })
  }
  if (q === 'pain') {
    if (selectedPainPoints.value.length === 0 && painPointOptions.length > 0) {
      selectedPainPoints.value = [painPointOptions[0].value]
    }
  }
  if (q === 'tools') {
    if (recommendedToolsUI.value.length > 0) {
      selectedTools.value = [recommendedToolsUI.value[0].id]
      updateTalkSelectedValues('tools')
    }
  }
})
function prevQuestion() {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value -= 1
  }
}
const currentStep = ref(1)
const openChat = ref(false)
const talks = ref<IMessage[]>(exampleTalks)
function handleSubmit(content: string) { talks.value.push({ role: 'user', content }) }
const onboardingSteps = [
  { id: 1, titleKey: 'onboarding.legacy.steps.connectStore.title', descriptionKey: 'onboarding.legacy.steps.connectStore.description', icon: 'store' },
  { id: 2, titleKey: 'onboarding.legacy.steps.connectAds.title', descriptionKey: 'onboarding.legacy.steps.connectAds.description', icon: 'lucide:megaphone' },
  { id: 3, titleKey: 'onboarding.legacy.steps.setupTracking.title', descriptionKey: 'onboarding.legacy.steps.setupTracking.description', icon: 'settings' },
  { id: 4, titleKey: 'onboarding.legacy.steps.finish.title', descriptionKey: 'onboarding.legacy.steps.finish.description', icon: 'lucide:check-circle' },
]
const currentStepData = computed(() => onboardingSteps[currentStep.value - 1])
function nextStep() {
  if (currentStep.value < onboardingSteps.length)
    currentStep.value += 1
}
function previousStep() {
  if (currentStep.value > 1)
    currentStep.value -= 1
}
function goToDashboard() { router.push('/dashboard') }
function goToReports() { router.push('/attribution') }

// 将 Onboarding 的四个模块问题注入到 AI 聊天窗口
function seedOnboardingMessages() {
  // 行业（单选）
  talks.value.push({
    role: 'system',
    content: t('onboarding.aiPrompts.industry'),
    type: 'options',
    questionKey: 'industry',
    multi: false,
    options: industryOptions.map(opt => ({ label: t(opt.labelKey), value: opt.value })),
    selectedValues: selectedIndustry.value ? [selectedIndustry.value] : [],
  })
  // 了解渠道（单选）
  talks.value.push({
    role: 'system',
    content: t('onboarding.aiPrompts.source'),
    type: 'options',
    questionKey: 'source',
    multi: false,
    options: sourceOptions.map(opt => ({ label: t(opt.labelKey), value: opt.value })),
    selectedValues: selectedSource.value ? [selectedSource.value] : [],
  })
  // 解决痛点（多选）
  talks.value.push({
    role: 'system',
    content: t('onboarding.aiPrompts.goals'),
    type: 'options',
    questionKey: 'painPoints',
    multi: true,
    options: painPointOptions.map(opt => ({ label: t(opt.labelKey), value: opt.value })),
    selectedValues: [...selectedPainPoints.value],
  })
}
// 首次注入
seedOnboardingMessages()

// 根据 painPoints 动态生成工具（多选）消息
function upsertToolsMessage() {
  // 查找是否已有 tools 消息
  const idx = talks.value.findIndex(t => t.type === 'options' && t.questionKey === 'tools')
  const msg = {
    role: 'system',
    content: t('onboarding.aiPrompts.tools'),
    type: 'options',
    questionKey: 'tools',
    multi: true,
    options: recommendedTools.value.map(tool => ({ label: t(tool.labelKey), value: tool.id })),
    selectedValues: [...selectedTools.value],
  } as IMessage
  if (recommendedTools.value.length === 0) {
    // 无推荐时移除消息
    if (idx >= 0)
      talks.value.splice(idx, 1)
    return
  }
  if (idx >= 0)
    talks.value.splice(idx, 1, msg)
  else talks.value.push(msg)
}

watch(recommendedTools, () => {
  upsertToolsMessage()
})

function updateTalkSelectedValues(questionKey: IMessage['questionKey']) {
  const m = talks.value.find(t => t.type === 'options' && t.questionKey === questionKey)
  if (!m)
    return
  if (questionKey === 'industry' && selectedIndustry.value)
    m.selectedValues = [selectedIndustry.value]
  if (questionKey === 'source' && selectedSource.value)
    m.selectedValues = [selectedSource.value]
  if (questionKey === 'painPoints')
    m.selectedValues = [...selectedPainPoints.value]
  if (questionKey === 'tools')
    m.selectedValues = [...selectedTools.value]
}

function handleChatSelect(payload: { questionKey?: IMessage['questionKey'], value: string, multi?: boolean }) {
  const key = payload.questionKey
  if (!key)
    return
  const val = payload.value

  if (key === 'industry') {
    selectedIndustry.value = val
  }
  else if (key === 'source') {
    selectedSource.value = val
  }
  else if (key === 'painPoints') {
    const i = selectedPainPoints.value.indexOf(val)
    if (i >= 0) {
      selectedPainPoints.value.splice(i, 1)
    }
    else {
      selectedPainPoints.value.push(val)
    }
  }
  else if (key === 'tools') {
    const i = selectedTools.value.indexOf(val)
    if (i >= 0)
      selectedTools.value.splice(i, 1)
    else selectedTools.value.push(val)
  }

  updateTalkSelectedValues(key)
}
const missingOtherMessages = computed(() => {
  const msgs: string[] = []
  if (selectedIndustry.value === 'Other' && industryOther.value.trim().length === 0) {
    msgs.push(t('onboarding.validation.industryOther'))
  }
  // Source is now mandatory for all options, not just 'Other'
  if (!selectedSource.value) {
    msgs.push(t('onboarding.validation.sourceSelect'))
  }
  else if (sourceOther.value.trim().length === 0) {
    msgs.push(t('onboarding.validation.sourceDetail'))
  }
  if (otherSelected.value && customToolName.value.trim().length === 0) {
    msgs.push(t('onboarding.validation.toolOther'))
  }
  return msgs
})
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <div class="mx-auto px-4 py-6 max-w-[1600px]">
      <div v-if="showPhase1" class="mb-10">
        <div class="mb-4 flex justify-center">
          <div class="inline-flex items-center gap-2">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {{ t('onboarding.questionnaire.title') }}
            </h2>
            <button type="button" class="mb-2 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-1 text-gray-600 hover:text-gray-900" @click="toggleOnboardingLocale">
              <Icon name="lucide:languages" type="iconify" class="h-4 w-4" />
              <span class="sr-only">{{ isZhLocale ? t('onboarding.translation.toEnglish') : t('onboarding.translation.toChinese') }}</span>
            </button>
          </div>
        </div>
        <div class="flex items-start gap-1">
          <OnboardingStepSidebarGoalFirst
            :current-question="currentQuestion"
            :goal-selected="selectedPainPoints.length > 0"
            :goal-other-focused="selectedPainPoints.includes('I have other goals')"
            :goal-other-filled="selectedPainPoints.includes('I have other goals') ? (painOther.trim().length > 0) : false"
            :tools-selected-count="selectedTools.length"
          />
          <Card class="overflow-hidden shadow-none flex-1 max-w-[1200px]">
            <CardContent class="p-0 h-[560px]">
              <div class="flex flex-col md:flex-row items-stretch h-full">
                <div class="flex-1 pt-0 px-10 pb-4">
                  <div class="space-y-2">
                    <Card v-if="currentQuestion === 'pain'" class="border-0 shadow-none">
                      <CardHeader class="p-0">
                        <CardTitle class="flex items-center gap-2">
                          <span>{{ t('onboarding.questionnaire.goals.title') }}</span>
                          <span class="text-gray-500 dark:text-gray-400 font-regular text-sm" />
                        </CardTitle>
                        <CardDescription>
                          {{ t('onboarding.questionnaire.goals.description') }}
                        </CardDescription>
                      </CardHeader>
                      <CardContent class="p-0 pt-1">
                        <div class="flex flex-col gap-2">
                          <button
                            v-for="pt in painPointOptions" :key="pt.value" class="w-full p-3 rounded-lg border text-sm text-left transition shadow-none focus:shadow-none" :class="[selectedPainPoints.includes(pt.value) ? 'border border-[#DFECE5] bg-[#DFECE5] text-[#286B49] font-semibold' : 'border-gray-200 hover:border-gray-300 dark:border-gray-700']"
                            @click="togglePainPoint(pt.value)"
                          >
                            {{ t(pt.labelKey) }}
                          </button>
                        </div>
                        <div v-if="selectedPainPoints.includes('I have other goals')" class="mt-3">
                          <Input
                            v-model="painOther"
                            :placeholder="t('onboarding.questionnaire.goals.otherPlaceholder')"
                            class="h-11 shadow-none focus:shadow-none ring-0 focus:ring-0 focus-visible:ring-0"
                            :class="[painOther.trim() === '' ? 'border-red-500 focus:border-[#286B49]' : '']"
                          />
                          <p v-if="painOther.trim() === ''" class="text-xs text-red-500 mt-1 font-medium">
                            {{ t('onboarding.questionnaire.required') }}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card v-if="currentQuestion === 'tools'" class="border-0 shadow-none">
                      <CardHeader class="p-0">
                        <CardTitle class="flex items-center gap-2">
                          <span>{{ t('onboarding.questionnaire.tools.title') }}</span>
                        </CardTitle>
                        <CardDescription>
                          {{ t('onboarding.questionnaire.tools.description') }}
                        </CardDescription>
                      </CardHeader>
                      <CardContent class="p-0 pt-1">
                        <div class="flex flex-wrap gap-2">
                          <button
                            v-for="tool in recommendedToolsUI" :key="tool.id" class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition shadow-none focus:shadow-none" :class="[
                              selectedTools.includes(tool.id) || (tool.id === 'other' && otherSelected)
                                ? 'border border-[#DFECE5] bg-[#DFECE5] text-[#286B49]'
                                : 'border-gray-200 hover:border-gray-300 dark:border-gray-700',
                            ]" @click="toggleTool(tool.id)"
                          >
                            <Icon :name="tool.icon" :type="tool.icon.includes(':') ? 'iconify' : 'local'" class="w-5 h-5" />
                            <span class="font-semibold">{{ t(tool.labelKey) }}</span>
                          </button>
                        </div>
                      </CardContent>
                    </Card>

                    <div class="flex border-t border-gray-200 mt-4 pt-4" :class="[isFirstQuestion ? 'justify-end' : 'justify-between']">
                      <Button v-if="!isFirstQuestion" variant="outline" class="bg-white border-[#286B49] text-[#286B49] hover:bg-gray-50" @click="prevQuestion">
                        {{ t('onboarding.actions.back') }}
                      </Button>
                      <div>
                        <Button v-if="!isLastQuestion" :disabled="!canNext" :class="[canNext ? 'bg-[#286B49] hover:bg-[#286B49]' : '']" @click="nextQuestion">
                          {{ t('onboarding.actions.continue') }}
                        </Button>
                        <Button v-else ref="continueBtnRef" :disabled="!canNext" :class="[canNext ? 'bg-[#286B49] hover:bg-[#286B49]' : '']" @click="handlePhase1Continue">
                          {{ t('onboarding.actions.continue') }}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="hidden md:block w-px bg-gray-200 self-stretch" />
                <div class="flex-1 p-0">
                  <div class="w-full h-full rounded-lg overflow-auto px-10">
                    <img v-if="currentQuestion === 'tools'" :src="toolsImageSrc" :alt="t('onboarding.questionnaire.images.tools')" class="w-full h-full object-cover block m-0 p-0">
                    <img v-else-if="currentQuestion === 'pain'" :src="painImageSrc" :alt="t('onboarding.questionnaire.images.goal')" class="w-full h-full object-contain block m-0 p-0">
                    <div v-else class="w-full h-full flex items-center justify-center text-gray-500">
                      {{ t('onboarding.questionnaire.images.placeholder') }}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <OnboardingHelpRow />
      </div>
      <div v-else>
        <div class="mb-8 flex justify-center">
          <div class="inline-flex items-center gap-2">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {{ t('onboarding.legacy.title') }}
            </h1>
            <button type="button" class="mb-4 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-1 text-gray-600 hover:text-gray-900" @click="toggleOnboardingLocale">
              <Icon name="lucide:languages" type="iconify" class="h-4 w-4" />
              <span class="sr-only">{{ isZhLocale ? t('onboarding.translation.toEnglish') : t('onboarding.translation.toChinese') }}</span>
            </button>
          </div>
        </div>
        <div class="mb-8 text-center">
          <p class="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {{ t('onboarding.legacy.description') }}
          </p>
        </div>
        <div class="mb-8">
          <div class="flex items-center justify-center space-x-4">
            <div v-for="(step, index) in onboardingSteps" :key="step.id" class="flex items-center">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium" :class="[currentStep >= index + 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400']"
              >
                {{ index + 1 }}
              </div>
              <div
                v-if="index < onboardingSteps.length - 1"
                class="w-16 h-0.5 mx-2" :class="[currentStep > index + 1 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700']"
              />
            </div>
          </div>
          <div class="flex justify-center mt-2">
            <span class="text-sm text-gray-600 dark:text-gray-400">{{ t('onboarding.legacy.stepCount', { current: currentStep, total: onboardingSteps.length }) }}</span>
          </div>
        </div>
        <div class="max-w-4xl mx-auto">
          <div v-if="currentStep === onboardingSteps.length && missingOtherMessages.length > 0" class="mb-4">
            <Alert>
              <AlertTitle>{{ t('onboarding.legacy.alertTitle') }}</AlertTitle>
              <AlertDescription>
                <ul class="list-disc pl-4">
                  <li v-for="m in missingOtherMessages" :key="m">
                    {{ m }}
                  </li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
          <Card v-if="currentStepData" class="mt-10 mb-6">
            <CardHeader>
              <CardTitle class="flex items-center space-x-2">
                <Icon :name="currentStepData.icon" type="local" class="w-6 h-6" /><span>{{ t(currentStepData.titleKey) }}</span>
              </CardTitle>
              <CardDescription>{{ t(currentStepData.descriptionKey) }}</CardDescription>
            </CardHeader>
            <CardContent>
              <div v-if="currentStep === 1" class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card class="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <div class="text-center">
                      <Icon name="simple-icons:shopify" class="w-12 h-12 mx-auto mb-2 text-green-600" />
                      <h3 class="font-medium mb-2">
                        {{ t('onboarding.legacy.shopify.title') }}
                      </h3>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {{ t('onboarding.legacy.shopify.description') }}
                      </p>
                      <Button variant="outline" size="sm">
                        {{ t('onboarding.legacy.shopify.action') }}
                      </Button>
                    </div>
                  </Card>
                  <Card class="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <div class="text-center">
                      <Icon name="simple-icons:woocommerce" class="w-12 h-12 mx-auto mb-2 text-purple-600" />
                      <h3 class="font-medium mb-2">
                        {{ t('onboarding.legacy.woocommerce.title') }}
                      </h3>
                      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {{ t('onboarding.legacy.woocommerce.description') }}
                      </p>
                      <Button variant="outline" size="sm">
                        {{ t('onboarding.legacy.woocommerce.action') }}
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
              <div v-else-if="currentStep === 2" class="space-y-4">
                <p class="text-sm text-gray-600">
                  {{ t('onboarding.legacy.placeholders.connectAds') }}
                </p>
              </div>
              <div v-else-if="currentStep === 3" class="space-y-4">
                <p class="text-sm text-gray-600">
                  {{ t('onboarding.legacy.placeholders.setupTracking') }}
                </p>
              </div>
              <div v-else-if="currentStep === 4" class="space-y-4">
                <p class="text-sm text-gray-600">
                  {{ t('onboarding.legacy.placeholders.done') }}
                </p>
                <div class="flex justify-center space-x-4">
                  <Button @click="goToDashboard">
                    {{ t('onboarding.legacy.actions.dashboard') }}
                  </Button>
                  <Button variant="outline" @click="goToReports">
                    {{ t('onboarding.legacy.actions.reports') }}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <div class="flex justify-end">
            <Button :disabled="currentStep === onboardingSteps.length" @click="nextStep">
              {{ currentStep === onboardingSteps.length ? t('onboarding.legacy.actions.finish') : t('onboarding.actions.continue') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
    <AiOnboardingAssistant v-model:open="showAiAssistant" />
  </div>
</template>

<style scoped>
/* 可在此处扩展页面样式 */
</style>
